import TagManagerDialog from './TagManagerDialog';
import TagPalette from './TagPalette';
import type { GuestTagWrites } from './useGuestTags';
import type { CreateTagKind } from '../../../lib/guestTags';
import type { GuestTag, TrackerGuest } from '../../../services/tracker-api-client';

/**
 * The two dialogs that write tags, and what closing each one does.
 *
 * Kept together because they share one rule that is easy to lose when they sit
 * apart: closing either has to clear the last refusal. A stale "12 guests still
 * have that tag" waiting inside a dialog the host reopens two minutes later is
 * an error about a state that no longer exists, and a host who sees one stops
 * reading the next.
 *
 * Rendered by the Guests tab rather than owning any state of its own — which
 * guests the palette is about is the tab's question, because the tab is what
 * knows the filter and the ticks.
 */
export default function GuestTagDialogs({
  vocabulary,
  allGuests,
  paletteFor,
  paletteKind,
  paletteGuests,
  managingTags,
  writes,
  onPaletteApplied,
  onClosePalette,
  onCloseManager,
}: {
  vocabulary: GuestTag[];
  allGuests: TrackerGuest[];
  /** Invitee ids the palette is about, or null when it is closed. */
  paletteFor: string[] | null;
  /** The kind the palette's Create starts on — the intent it was opened with. */
  paletteKind: CreateTagKind;
  paletteGuests: TrackerGuest[];
  managingTags: boolean;
  writes: GuestTagWrites;
  /** Only called when the apply actually landed. */
  onPaletteApplied: () => void;
  onClosePalette: () => void;
  onCloseManager: () => void;
}) {
  return (
    <>
      {paletteFor ? (
        <TagPalette
          guests={paletteGuests}
          vocabulary={vocabulary}
          allGuests={allGuests}
          busy={writes.busy}
          error={writes.error}
          defaultKind={paletteKind}
          onApply={(apply) => {
            /*
              The guests the PLAN was computed against, not the ids that opened
              the dialog. A guest deleted in another tab drops out of
              `paletteGuests`, and sending the raw list would write to an id the
              API refuses — failing the whole transaction over somebody who is
              not there any more.
            */
            const inviteeIds = paletteGuests.map((guest) => guest.inviteeId);
            void writes.assign(apply, inviteeIds).then((ok) => {
              // A refused apply leaves the dialog open with its error on
              // screen. Closing it would take the reason away with it.
              if (ok) onPaletteApplied();
            });
          }}
          onCreate={(label, kind) => void writes.create(label, kind)}
          onClose={() => {
            writes.clearError();
            onClosePalette();
          }}
        />
      ) : null}

      {managingTags ? (
        <TagManagerDialog
          tags={vocabulary}
          guests={allGuests}
          busy={writes.busy}
          error={writes.error}
          onEdit={(tagId, edit) => void writes.edit(tagId, edit)}
          onDelete={(tagId, force) => void writes.remove(tagId, force)}
          onClose={() => {
            writes.clearError();
            onCloseManager();
          }}
        />
      ) : null}
    </>
  );
}
